import React from 'react';
import ShotLocationPicker, { PickedLocation } from '../../../components/shotChart/ShotLocationPicker';
import { ShotOutcome } from '../../../utils/shotLocation';

interface PlayerSelectionModalProps {
  open: boolean;
  players: any[];
  title: string;
  subtitle: string;
  selectedPlayer: number | string | null;
  onSelectPlayer: (number: number | string) => void;
  onConfirm: () => void;
  onClose: () => void;
  showConfirmButton?: boolean;
  showShotClockViolationButton?: boolean;
  onShotClockViolation?: () => void;
  showTeamStatButton?: boolean;
  onTeamStat?: () => void;
  submitting?: boolean;
  /** Optional shot-location picker rendered above the roster grid (never blocks selection). */
  showLocationPicker?: boolean;
  locationOutcome?: ShotOutcome | null;
  locationValue?: PickedLocation | null;
  onLocationChange?: (value: PickedLocation | null) => void;
  /** US capture can hide the field. A follow-up THEM step keeps it on screen. */
  locationCollapsible?: boolean;
  /** Place the field above the roster prompt. */
  locationFirst?: boolean;
  locationTitle?: string;
}

const PlayerSelectionModal: React.FC<PlayerSelectionModalProps> = ({
  open,
  players,
  title,
  subtitle,
  selectedPlayer,
  onSelectPlayer,
  onConfirm,
  onClose,
  showConfirmButton = false,
  showShotClockViolationButton = false,
  onShotClockViolation,
  showTeamStatButton = false,
  onTeamStat,
  submitting = false,
  showLocationPicker = false,
  locationOutcome = null,
  locationValue = null,
  onLocationChange,
  locationCollapsible = true,
  locationFirst = false,
  locationTitle,
}) => {
  // Disable body scroll when modal is open
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Clean up on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  if (!open) return null;

  // Handle backdrop click to close modal
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (!submitting && e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirmClick = () => {
    if (submitting) return;
    onConfirm();
  };

  const handleCloseClick = () => {
    if (submitting) return;
    onClose();
  };

  const handleShotClockViolationClick = () => {
    if (submitting || !onShotClockViolation) return;
    onShotClockViolation();
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="relative bg-white shadow-lg p-0 flex flex-col items-center w-full h-[100vh]">
        <div className="relative w-full h-[120px] flex items-center justify-center overflow-hidden">
          <img
            src="/images/goal-modal-header.png"
            alt="goal"
            className="absolute top-0 left-0 w-full h-full object-cover z-[1]"
          />
          <p className="relative text-center italic font-black text-[40px] md:text-[45px] lg:text-[54px] text-sd-dark z-20 text-stroke-green">
            {title}
          </p>
        </div>
        <div className="w-full flex flex-col items-center px-4 pt-4 pb-6 overflow-y-auto">
          {locationFirst && showLocationPicker && onLocationChange && (
            <div className="w-full max-w-xl mb-4">
              <ShotLocationPicker
                outcome={locationOutcome}
                value={locationValue}
                onChange={onLocationChange}
                title={locationTitle}
                collapsible={locationCollapsible}
              />
            </div>
          )}
          <p className="text-2xl font-bold text-center text-sd-dark mb-3 w-full">{subtitle}</p>
          {!locationFirst && showLocationPicker && onLocationChange && (
            <div className="w-full max-w-xl mb-5 pb-4 border-b border-sd-line">
              <ShotLocationPicker
                outcome={locationOutcome}
                value={locationValue}
                onChange={onLocationChange}
                title={locationTitle}
                collapsible={locationCollapsible}
              />
            </div>
          )}
          <div className="grid grid-cols-5 gap-5 mb-6 w-full">
            {players.map((player: any, idx: number) => (
              <div
                className="flex flex-col justify-around items-center"
                key={idx}
                onClick={() => {
                  if (submitting) return;
                  onSelectPlayer(player.no);
                }}
              >
                <button
                  key={player.no}
                  className={`flex flex-col items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full font-semibold text-white text-base shadow transition-all duration-150
                    ${
                      selectedPlayer === player.no
                        ? 'bg-[#fff59d] text-black border-2 border-yellow-400'
                        : 'bg-sd-dark'
                    }
                  `}
                >
                  <span className="text-[20px]">{player.no}</span>
                </button>
                <p
                  className="w-full text-sm font-semibold leading-tight text-center px-0.5 min-h-[2.5em]"
                  title={`${player.firstName} ${player.lastName}`}
                >
                  <span className="block truncate">{player.firstName}</span>
                  <span className="block truncate">{player.lastName}</span>
                </p>
              </div>
            ))}

            {/* Team Stat Option - in same grid as players */}
            {showTeamStatButton && (
              <div
                className="flex flex-col justify-around items-center"
                onClick={() => {
                  if (submitting) return;
                  if (onTeamStat) {
                    onTeamStat();
                  }
                }}
              >
                <button
                  className={`flex flex-col items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full font-semibold text-white text-base shadow transition-all duration-150
                    ${selectedPlayer === 'team' ? 'bg-[#fff59d] text-black border-2 border-yellow-400' : 'bg-[#ff6b35]'}
                  `}
                >
                  <span className="text-[20px]">T</span>
                </button>
                <p className="w-full text-sm font-semibold leading-tight text-center px-0.5 min-h-[2.5em]">
                  <span className="block truncate">Team</span>
                  <span className="block truncate">Stat</span>
                </p>
              </div>
            )}
          </div>
          <div className="flex gap-4 mt-4">
            {showConfirmButton && selectedPlayer && (
              <button className="px-4 py-2 text-white bg-sd-dark rounded font-semibold" onClick={handleConfirmClick}>
                {submitting ? 'Saving...' : 'Confirm'}
              </button>
            )}
            {showShotClockViolationButton && (
              <button
                className="px-4 py-2 text-white bg-[#ff6b35] rounded font-semibold"
                onClick={handleShotClockViolationClick}
              >
                Shot Clock Violation
              </button>
            )}
            <button className="px-4 py-2 text-black bg-sd-green rounded font-semibold" onClick={handleCloseClick}>
              {submitting ? 'Saving...' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerSelectionModal;
